import React, { useState } from "react";
import { ethers } from "ethers";
import { useWeb3 } from "../context/Web3Context";
import { useReads } from "../hooks/useReads";
import { getEthersProvider } from "../web3/provider";
import { getCoreMining } from "../web3/contracts";
import { useToast } from "../components/common/Toast";
import { fmtUnits, shortAddress, parseUsdt } from "../utils/format";
import { ACTIVE_CONTRACTS, CORE_DECIMALS } from "../config/app.config";
import StatGrid from "../components/admin/StatGrid";
import ActionForm from "../components/admin/ActionForm";
import PauseControl from "../components/admin/PauseControl";
import RoleBanner from "../components/admin/RoleBanner";
import AddressBar from "../components/admin/AddressBar";
import SectionHeader from "../components/common/SectionHeader";

const addr = (k, label, ph = "0x...") => ({ key: k, label, placeholder: ph });
const core = (x) => `${fmtUnits(x, CORE_DECIMALS, 2)} CORE`;

export default function CoreMiningAdmin() {
  const { signer } = useWeb3();
  const { data, reload } = useReads(async (provider) => {
    const m = getCoreMining(provider);
    const [owner, coreToken, smartcore, stats] = await Promise.all([
      m.owner(), m.coreToken(), m.smartcoreContract(), m.getGlobalStats(),
    ]);
    return { owner, coreToken, smartcore, stats };
  });

  const C = (s) => getCoreMining(s);

  return (
    <div className="stack">
      <AddressBar label="CORE Mining Contract" address={ACTIVE_CONTRACTS.coreMining} />
      <RoleBanner owner={data?.owner} admin={data?.owner} />

      <StatGrid
        title="CORE Mining — Overview" icon="spark" accent="violet" onRefresh={reload}
        rows={data ? [
          { label: "Owner", value: shortAddress(data.owner), mono: true },
          { label: "Paused", value: data.stats._paused ? "Yes" : "No" },
          { label: "Current Day (UTC)", value: data.stats._currentDay.toString() },
          { label: "Total Miners", value: data.stats._totalMiners.toString() },
          { label: "Energy Distributed", value: core(data.stats._totalEnergyDistributed) },
          { label: "Tokens Mined", value: core(data.stats._totalTokensMined) },
          { label: "Tokens Withdrawn", value: core(data.stats._totalTokensWithdrawn) },
          { label: "CORE Token", value: shortAddress(data.coreToken), mono: true },
          { label: "Smartcore", value: shortAddress(data.smartcore), mono: true },
        ] : []}
      />

      <MinerLookup />

      <PauseControl paused={data?.stats?._paused} getContract={C} signer={signer} onDone={reload} />

      <div className="adm-section-title">Mining operations</div>
      <div className="adm-grid">
        <ActionForm title="Admin Add Energy" desc="Add a mining session for a Smartcore user. Investment in USD (1$ = 0.1 CORE/day, 1 day per $1)." icon="spark" accent="gold"
          fields={[addr("a", "User address"), { key: "amt", label: "Investment (USD)", placeholder: "100", numeric: true }]}
          button="Add Energy" onDone={reload}
          onRun={(v) => C(signer).adminAddEnergy(v.a, parseUsdt(v.amt))} />
      </div>

      <div className="adm-section-title">Wiring & roles</div>
      <div className="adm-grid">
        <ActionForm title="Set CORE Token" icon="coins" fields={[addr("a", "CORE token address")]} button="Set CORE Token" onDone={reload} onRun={(v) => C(signer).setCoreToken(v.a)} />
        <ActionForm title="Set Smartcore Contract" icon="node" fields={[addr("a", "SmartcoreNodePurchase address")]} button="Set Smartcore" onDone={reload} onRun={(v) => C(signer).setSmartcoreContract(v.a)} />
        <ActionForm title="Transfer Ownership" desc="Irreversible." icon="shield" danger fields={[addr("a", "New owner address")]} button="Transfer Ownership" onDone={reload} onRun={(v) => C(signer).transferOwnership(v.a)} />
      </div>
    </div>
  );
}

function MinerLookup() {
  const toast = useToast();
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [res, setRes] = useState(null);

  const search = async () => {
    const query = q.trim();
    if (!ethers.utils.isAddress(query)) { toast.error("Enter a valid 0x wallet address."); return; }
    setLoading(true); setRes(null);
    try {
      const m = getCoreMining(getEthersProvider());
      const s = await m.getUserStats(query);
      setRes(s);
    } catch {
      toast.error("Lookup failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card">
      <SectionHeader icon="users" title="Miner Lookup" accent="cyan" />
      <div style={{ display: "flex", gap: 8 }}>
        <input className="input" placeholder="Wallet address (0x…)" value={q}
          onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()} />
        <button className="btn" style={{ flex: "0 0 auto", padding: "0 18px" }} onClick={search} disabled={loading}>
          {loading ? "…" : "Search"}
        </button>
      </div>
      {res && (
        <div className="lookup-result fade-up">
          <div className="stat-rows">
            <div className="stat-row"><span className="muted">Smartcore ID</span><span>{res.smartcoreUserId.toString()}</span></div>
            <div className="stat-row"><span className="muted">Total Energy</span><span>{core(res.totalEnergy)}</span></div>
            <div className="stat-row"><span className="muted">Available Energy</span><span>{core(res.availableEnergy)}</span></div>
            <div className="stat-row"><span className="muted">Total Mined</span><span>{core(res.totalMined)}</span></div>
            <div className="stat-row"><span className="muted">Pending</span><span>{core(res.pendingBalance)}</span></div>
            <div className="stat-row"><span className="muted">Withdrawable</span><span>{core(res.withdrawableBalance)}</span></div>
            <div className="stat-row"><span className="muted">Total Withdrawn</span><span>{core(res.totalWithdrawn)}</span></div>
            <div className="stat-row"><span className="muted">Sessions</span><span>{res.sessionCount.toString()}</span></div>
            <div className="stat-row"><span className="muted">Can Mine Today</span><span>{res.canMineToday ? "Yes" : "No"}</span></div>
          </div>
        </div>
      )}
    </div>
  );
}
